/** Supabase Storage utilities for private deck images and original files. */

import { getSupabaseBrowserClient } from './supabase';
import { getImage } from '../app/lib/imageCache';
import { API_URL } from '../app/lib/api';
import type { FlashCard } from '../app/lib/api';

const IMAGES_BUCKET = 'deck-images';
const FILES_BUCKET = 'deck-files';
const STORAGE_PREFIX = 'storage://';

type StorageReference = { bucket: string; path: string };

function requireSupabase() {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud storage is not configured');
  return supabase;
}

export function toStorageReference(bucket: string, path: string): string {
  return `${STORAGE_PREFIX}${bucket}/${path}`;
}

/** Parse both durable storage:// refs and legacy Supabase public/signed URLs. */
export function parseStorageReference(value: string): StorageReference | null {
  if (value.startsWith(STORAGE_PREFIX)) {
    const rest = value.slice(STORAGE_PREFIX.length);
    const slash = rest.indexOf('/');
    if (slash <= 0 || slash === rest.length - 1) return null;
    return { bucket: rest.slice(0, slash), path: rest.slice(slash + 1) };
  }

  try {
    const url = new URL(value);
    const marker = '/storage/v1/object/';
    const markerIndex = url.pathname.indexOf(marker);
    if (markerIndex < 0) return null;
    const parts = url.pathname.slice(markerIndex + marker.length).split('/');
    if (['public', 'sign', 'authenticated'].includes(parts[0])) parts.shift();
    const bucket = parts.shift();
    if (!bucket || parts.length === 0) return null;
    return { bucket: decodeURIComponent(bucket), path: parts.map(decodeURIComponent).join('/') };
  } catch {
    return null;
  }
}

export function normalizeStorageReference(value?: string | null): string | null {
  if (!value) return null;
  const reference = parseStorageReference(value);
  return reference ? toStorageReference(reference.bucket, reference.path) : value;
}

export async function resolveStorageReference(value?: string | null): Promise<string | null> {
  if (!value) return null;
  const reference = parseStorageReference(value);
  if (!reference) return value;
  const supabase = requireSupabase();
  const { data, error } = await supabase.storage
    .from(reference.bucket)
    .createSignedUrl(reference.path, 24 * 60 * 60);
  if (error || !data?.signedUrl) throw error || new Error('Could not create a private file link');
  return data.signedUrl;
}

export function normalizeCardStorageReferences(cards: FlashCard[]): FlashCard[] {
  return cards.map(card => ({
    ...card,
    image: normalizeStorageReference(card.image) || undefined,
    occludedImage: normalizeStorageReference(card.occludedImage) || undefined,
  }));
}

export async function hydrateCardStorageUrls(cards: FlashCard[]): Promise<FlashCard[]> {
  const cache = new Map<string, Promise<string | null>>();
  const resolve = (value?: string | null) => {
    if (!value) return Promise.resolve(null);
    if (!cache.has(value)) cache.set(value, resolveStorageReference(value));
    return cache.get(value)!;
  };
  return Promise.all(cards.map(async card => ({
    ...card,
    image: (await resolve(card.image)) || undefined,
    occludedImage: (await resolve(card.occludedImage)) || undefined,
  })));
}

function safeFilename(value: string, fallback: string): string {
  const withoutQuery = value.split(/[?#]/, 1)[0];
  const basename = withoutQuery.split('/').pop() || fallback;
  const sanitized = basename.replace(/[^a-zA-Z0-9._-]/g, '_');
  return sanitized || fallback;
}

function shortHash(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export async function uploadImageToStorage(
  userId: string,
  deckId: string,
  filename: string,
  blob: Blob,
): Promise<string> {
  const supabase = requireSupabase();
  const path = `${userId}/${deckId}/${safeFilename(filename, 'image.png')}`;
  const { error } = await supabase.storage.from(IMAGES_BUCKET).upload(path, blob, {
    contentType: blob.type || 'image/png',
    upsert: true,
  });
  if (error) throw error;
  return toStorageReference(IMAGES_BUCKET, path);
}

async function fetchImageBlob(sessionId: string | null | undefined, source: string): Promise<Blob> {
  let url = source;
  const reference = parseStorageReference(source);
  if (reference && source.startsWith(STORAGE_PREFIX)) {
    url = (await resolveStorageReference(source))!;
  } else if (!/^https?:\/\//i.test(source) && !source.startsWith('blob:') && !source.startsWith('data:')) {
    if (!sessionId) throw new Error(`Image ${source} is no longer available`);
    url = await getImage(API_URL, sessionId, source);
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Image download failed (${response.status})`);
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) throw new Error('Stored card asset is not an image');
    return blob;
  } finally {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}

/** Copy every referenced card image into the destination deck's private folder. */
export async function persistCardImages(
  userId: string,
  deckId: string,
  cards: FlashCard[],
  sessionId?: string | null,
): Promise<FlashCard[]> {
  const copied = new Map<string, Promise<string>>();
  const persist = (source: string, fallback: string): Promise<string> => {
    if (!copied.has(source)) {
      copied.set(source, (async () => {
        const blob = await fetchImageBlob(sessionId, source);
        const filename = `${shortHash(source)}-${safeFilename(source, fallback)}`;
        return uploadImageToStorage(userId, deckId, filename, blob);
      })());
    }
    return copied.get(source)!;
  };

  return Promise.all(cards.map(async (card, index) => ({
    ...card,
    image: card.image ? await persist(card.image, `${card.id || index}-image.png`) : undefined,
    occludedImage: card.occludedImage
      ? await persist(card.occludedImage, `${card.id || index}-occluded.png`)
      : undefined,
  })));
}

export async function uploadOriginalFile(userId: string, deckId: string, file: File): Promise<string> {
  const supabase = requireSupabase();
  const filename = safeFilename(file.name, 'original-file');
  const path = `${userId}/${deckId}/${filename}`;
  const { error } = await supabase.storage.from(FILES_BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: true,
  });
  if (error) throw error;
  return toStorageReference(FILES_BUCKET, path);
}

async function deleteStorageFolder(bucket: string, userId: string, deckId: string): Promise<void> {
  const supabase = requireSupabase();
  const folderPath = `${userId}/${deckId}`;
  const { data: files, error: listError } = await supabase.storage.from(bucket).list(folderPath);
  if (listError) throw listError;
  if (!files?.length) return;
  const { error } = await supabase.storage.from(bucket).remove(files.map(file => `${folderPath}/${file.name}`));
  if (error) throw error;
}

export async function deleteOriginalFile(userId: string, deckId: string): Promise<void> {
  await deleteStorageFolder(FILES_BUCKET, userId, deckId);
}

export async function deleteDecksImages(userId: string, deckId: string): Promise<void> {
  await deleteStorageFolder(IMAGES_BUCKET, userId, deckId);
}
